import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

/**
 * Shared create/edit broker modal — same fields and behaviour as Brokers page.
 */
export function BrokerFormModal({
  open,
  onClose,
  mode = 'create',
  record = null,
  onSaved,
  formId = 'broker-form',
}) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [nameError, setNameError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  const resetForm = useCallback(() => {
    if (mode === 'edit' && record) {
      setName(String(record.name || ''));
      setContact(String(record.contact || ''));
    } else {
      setName('');
      setContact('');
    }
    setNameError('');
    setFormError('');
  }, [mode, record]);

  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    if (!formError) return undefined;
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setNameError('');
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Please enter name');
      return;
    }

    setSaving(true);
    try {
      const isCreate = mode === 'create';
      const response = await fetch(
        isCreate ? `${API_BASE_URL}/api/brokers` : `${API_BASE_URL}/api/brokers/${record.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify({
            name: trimmedName,
            contact: contact.trim() || null,
          }),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save broker');
      }
      const saved = await response.json();
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setFormError(err?.message || 'Unable to save broker');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="md"
      title={mode === 'create' ? 'Add Broker' : 'Edit Broker'}
      description="Name is required; add contact details as you prefer."
      footer={(
        <>
          <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
            Reset
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={saving} disabled={saving}>
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      )}
    >
      <form id={formId} onSubmit={handleSubmit} className="py-6 space-y-4">
        <AlertMessage message={formError} type="error" onClose={() => setFormError('')} />
        <Input
          label="Name *"
          placeholder="Broker name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameError}
          required
        />
        <Input
          label="Contact"
          placeholder="Phone, WhatsApp, email…"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
        />
      </form>
    </Modal>
  );
}
